import { useEffect, useState } from "react";
import {
    PresenteeismConciliationDetailService,
    PresenteeismConciliationDetailServiceModel,
} from "@services/PresenteeismConciliationDetailService";
import { Description } from "@shared/utils/DescriptionsHelper";

export interface PresenteeismConciliationDetailRowRequest {
    organizationID: string;
    requesterID: string;
    day: string;
    tableRecordKey: string;
    buildingID?: string;
    checkInTimestamp?: number;
    collaboratorName?: string;
}

interface UsePresenteeismConciliationDetailModalViewModelParams {
    isOpen: boolean;
    request: PresenteeismConciliationDetailRowRequest | null;
    service: PresenteeismConciliationDetailService;
    timezone: string;
}

export interface DetailDisplayField {
    label: string;
    value?: string | number | null;
    url?: string | null;
}

export interface DetailMapData {
    markers: Array<{
        id: string;
        status: string;
        latitude: number;
        longitude: number;
        title: string;
    }>;
    circles: Array<{
        id: string;
        center: {
            latitude: number;
            longitude: number;
        };
        radiusInMeters: number;
        strokeColor: string;
        fillColor: string;
    }>;
}

export interface DetailDisplaySection {
    title: string;
    fields: DetailDisplayField[];
    mapData?: DetailMapData;
}

const formatMinutes = (minutes?: number | null) => {
    if (minutes == null || !Number.isFinite(minutes)) {
        return null;
    }

    const roundedMinutes = Math.round(minutes);
    const hours = Math.floor(roundedMinutes / 60);
    const remainingMinutes = roundedMinutes % 60;

    return `${hours.toString().padStart(2, "0")}:${remainingMinutes.toString().padStart(2, "0")}`;
};

const formatTimestamp = (timestamp: number | undefined, timezone: string) => {
    return timestamp != null ? Description.Timestamp.date(timestamp, timezone) : null;
};

const formatBoolean = (value?: boolean) => {
    if (value == null) {
        return null;
    }

    return value ? "Si".localized() : "No".localized();
};

const resolveBuildingName = (
    building?: PresenteeismConciliationDetailServiceModel.Building
) => {
    return building?.name || building?.buildingName || building?.title || null;
};

const resolveCreator = (
    audit?: PresenteeismConciliationDetailServiceModel.AuditInfo
) => {
    return audit?.createdByName || audit?.createdBy || null;
};

const resolveTriggerDistanceInMeters = (
    qontactPoint?: PresenteeismConciliationDetailServiceModel.QontactPoint
) => {
    const triggerDistance = Number(qontactPoint?.triggerDistance);

    return Number.isFinite(triggerDistance) && triggerDistance > 0
        ? triggerDistance
        : 50;
};

const formatCoordinates = (
    location?: PresenteeismConciliationDetailServiceModel.Location
) => {
    return location ? `${location.latitude}, ${location.longitude}` : null;
};

const buildGoogleMapsCoordinatesURL = (
    location?: PresenteeismConciliationDetailServiceModel.Location
) => {
    return location ? `https://www.google.com/maps?q=${location.latitude},${location.longitude}` : null;
};

const buildTicketURL = (ticketID?: string | null) => {
    return ticketID ? `https://tickets.qontact.com.ar/tickets/${ticketID}` : null;
};

const resolveBuildingCoordinates = (
    building?: PresenteeismConciliationDetailServiceModel.Building
) => {
    return building?.latitude != null && building?.longitude != null
        ? {
            latitude: building.latitude,
            longitude: building.longitude,
        }
        : null;
};

const buildRecordMapData = ({
    recordLocation,
    referenceBuilding,
    qontactPoint,
    recordMarkerTitle,
    referenceMarkerTitle,
}: {
    recordLocation?: PresenteeismConciliationDetailServiceModel.Location;
    referenceBuilding?: PresenteeismConciliationDetailServiceModel.Building;
    qontactPoint?: PresenteeismConciliationDetailServiceModel.QontactPoint;
    recordMarkerTitle: string;
    referenceMarkerTitle: string;
}): DetailMapData | undefined => {
    const referenceCoordinates = resolveBuildingCoordinates(referenceBuilding);
    const radiusInMeters = resolveTriggerDistanceInMeters(qontactPoint);

    if (!recordLocation || !referenceCoordinates) {
        return undefined;
    }

    return {
        markers: [
            {
                id: "record-location",
                status: "GREEN",
                latitude: recordLocation.latitude,
                longitude: recordLocation.longitude,
                title: recordMarkerTitle,
            },
            {
                id: "qontact-point-building",
                status: "DARK_GRAY",
                latitude: referenceCoordinates.latitude,
                longitude: referenceCoordinates.longitude,
                title: referenceMarkerTitle,
            },
        ],
        circles: [
            {
                id: "qontact-point-radius",
                center: referenceCoordinates,
                radiusInMeters,
                strokeColor: "#EC6C56",
                fillColor: "#EC6C56",
            },
        ],
    };
};

export const usePresenteeismConciliationDetailModalViewModel = ({
    isOpen,
    request,
    service,
    timezone,
}: UsePresenteeismConciliationDetailModalViewModelParams) => {
    const [detail, setDetail] = useState<PresenteeismConciliationDetailServiceModel.ConciliationDetail | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [cancelReason, setCancelReason] = useState("");
    const [canceling, setCanceling] = useState(false);
    const [cancelError, setCancelError] = useState<string | null>(null);
    const [undoingCancelation, setUndoingCancelation] = useState(false);
    const [undoCancelationError, setUndoCancelationError] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen || !request) {
            setDetail(null);
            setLoading(false);
            setError(null);
            setCancelReason("");
            setCanceling(false);
            setCancelError(null);
            setUndoingCancelation(false);
            setUndoCancelationError(null);
            return;
        }

        const controller = new AbortController();
        setLoading(true);
        setError(null);

        service.getDetail({
            ...request,
            signal: controller.signal,
        })
            .then(setDetail)
            .catch((fetchError) => {
                if (controller.signal.aborted) {
                    return;
                }

                console.error(fetchError);
                setDetail(null);
                setError("No se pudo cargar el detalle de la conciliación.".localized());
            })
            .finally(() => {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            });

        return () => {
            controller.abort();
        };
    }, [isOpen, request, service]);

    const cancelConciliation = async (): Promise<boolean> => {
        if (!detail || !detail.organizationID || !detail.checkinRecordID || !cancelReason.trim()) {
            return false;
        }

        setCanceling(true);
        setCancelError(null);

        try {
            const updatedDetail = await service.cancelConciliation({
                organizationID: detail.organizationID,
                checkinRecordID: detail.checkinRecordID,
                reason: cancelReason.trim(),
            });
            setDetail(updatedDetail);
            setCancelReason("");
            return true;
        } catch (cancelFetchError) {
            console.error(cancelFetchError);
            setCancelError("No se pudo cancelar la conciliación.".localized());
            return false;
        } finally {
            setCanceling(false);
        }
    };

    const resetCancelationDraft = () => {
        setCancelReason("");
        setCancelError(null);
    };

    const undoCancelation = async (): Promise<boolean> => {
        if (!detail || !detail.organizationID || !detail.checkinRecordID || !detail.isCanceled) {
            return false;
        }

        setUndoingCancelation(true);
        setUndoCancelationError(null);

        try {
            const updatedDetail = await service.undoCancelation({
                organizationID: detail.organizationID,
                checkinRecordID: detail.checkinRecordID,
            });
            setDetail(updatedDetail);
            return true;
        } catch (undoError) {
            console.error(undoError);
            setUndoCancelationError("No se pudo restaurar la conciliación.".localized());
            return false;
        } finally {
            setUndoingCancelation(false);
        }
    };

    const resetUndoCancelationDraft = () => {
        setUndoCancelationError(null);
    };

    const buildSections = (): DetailDisplaySection[] => {
        if (!detail) {
            return [];
        }

        const conciliation = detail.conciliationInfo ?? {};
        const checkinRecord = detail.checkinRecord ?? {};
        const checkoutRecord = detail.checkoutRecord ?? {};
        const checkinAudit = detail.checkinAuditInfo;
        const checkoutAudit = detail.checkoutAuditInfo;
        const checkinBuilding = detail.abConciliationInfo?.checkinBuilding
            ?? detail.checkinBuilding
            ?? conciliation.abConciliationCheckinBuilding;
        const checkoutBuilding = detail.abConciliationInfo?.checkoutBuilding
            ?? detail.checkoutBuilding
            ?? conciliation.abConciliationCheckoutBuilding;
        const checkinLocation = detail.checkinLocation ?? checkinRecord.location;
        const checkoutLocation = detail.checkoutLocation ?? checkoutRecord.location;
        const checkinTicketID = checkinRecord.ticketID ?? conciliation.checkinTicketID;
        const checkoutTicketID = checkoutRecord.ticketID ?? conciliation.checkoutTicketID;
        const canceledByName = detail.canceledBy?.name
            ?? detail.canceledBy?.id
            ?? detail.canceledBy?.sub;
        const summaryFields: DetailDisplayField[] = [
            { label: "Número".localized(), value: detail.requesterID },
            { label: "Día".localized(), value: detail.checkinRecord?.day ?? request?.day ?? detail.tableRecordKey },
        ];

        if (detail.isCanceled) {
            summaryFields.push(
                { label: "Cancelada".localized(), value: formatBoolean(detail.isCanceled) },
                { label: "Motivo cancelación".localized(), value: detail.canceledReason },
                { label: "Cancelada por".localized(), value: canceledByName },
                { label: "Fecha cancelación".localized(), value: formatTimestamp(detail.canceledAt, timezone) },
                { label: "ID undo cancelación".localized(), value: detail.cancellationUndoID },
            );
        }

        const result: DetailDisplaySection[] = [
            {
                title: "Resumen".localized(),
                fields: summaryFields,
            },
            {
                title: "Entrada".localized(),
                fields: [
                    { label: "Fecha".localized(), value: formatTimestamp(checkinRecord.timestamp ?? conciliation.checkinTimestamp, timezone) },
                    { label: "Record ID".localized(), value: detail.checkinRecordID },
                    { label: "Ticket".localized(), value: checkinTicketID, url: buildTicketURL(checkinTicketID) },
                    { label: "Coordenadas".localized(), value: formatCoordinates(checkinLocation), url: buildGoogleMapsCoordinatesURL(checkinLocation) },
                    { label: "Qontact Point".localized(), value: detail.checkinQontactPoint?.id },
                    {
                        label: "Radio permitido".localized(),
                        value: `${resolveTriggerDistanceInMeters(detail.checkinQontactPoint).toFixed(2)} m`,
                    },
                    { label: "Responsable registro manual".localized(), value: resolveCreator(checkinAudit) },
                    { label: "Fecha registro manual".localized(), value: formatTimestamp(checkinAudit?.timestamp, timezone) },
                ],
                mapData: buildRecordMapData({
                    recordLocation: checkinLocation,
                    referenceBuilding: checkinBuilding,
                    qontactPoint: detail.checkinQontactPoint,
                    recordMarkerTitle: "Ubicación de entrada".localized(),
                    referenceMarkerTitle: resolveBuildingName(checkinBuilding) ?? "Qontact Point de entrada".localized(),
                }),
            },
            {
                title: "Salida".localized(),
                fields: [
                    { label: "Fecha".localized(), value: formatTimestamp(checkoutRecord.timestamp ?? conciliation.checkoutTimestamp, timezone) },
                    { label: "Record ID".localized(), value: detail.checkoutRecordID },
                    { label: "Ticket".localized(), value: checkoutTicketID, url: buildTicketURL(checkoutTicketID) },
                    { label: "Coordenadas".localized(), value: formatCoordinates(checkoutLocation), url: buildGoogleMapsCoordinatesURL(checkoutLocation) },
                    { label: "Qontact Point".localized(), value: detail.checkoutQontactPoint?.id },
                    {
                        label: "Radio permitido".localized(),
                        value: `${resolveTriggerDistanceInMeters(detail.checkoutQontactPoint).toFixed(2)} m`,
                    },
                    { label: "Responsable registro manual".localized(), value: resolveCreator(checkoutAudit) },
                    { label: "Fecha registro manual".localized(), value: formatTimestamp(checkoutAudit?.timestamp, timezone) },
                ],
                mapData: buildRecordMapData({
                    recordLocation: checkoutLocation,
                    referenceBuilding: checkoutBuilding,
                    qontactPoint: detail.checkoutQontactPoint,
                    recordMarkerTitle: "Ubicación de salida".localized(),
                    referenceMarkerTitle: resolveBuildingName(checkoutBuilding) ?? "Qontact Point de salida".localized(),
                }),
            },
            {
                title: "Conciliación".localized(),
                fields: [
                    { label: "Horas trabajadas".localized(), value: formatMinutes(conciliation.minutesWorked) },
                    { label: "Horas esperadas".localized(), value: formatMinutes(conciliation.expectedMinutes) },
                    { label: "Horas extra".localized(), value: formatMinutes(conciliation.minutesExtraTime) },
                    { label: "Horas nocturnas".localized(), value: formatMinutes(conciliation.nightMinutes) },
                    { label: "Llegada tarde".localized(), value: formatBoolean(conciliation.isLateCheckin) },
                    { label: "Salida temprana".localized(), value: formatBoolean(conciliation.isEarlyCheckout) },
                    { label: "Salida en otro día".localized(), value: formatBoolean(conciliation.isCheckoutDayOverriden) },
                    { label: "Turno dedicado".localized(), value: formatBoolean(conciliation.isUserDedicatedShift) },
                ],
            },
        ];

        if (conciliation.isABConciliation) {
            result.push({
                title: "Conciliación AB".localized(),
                fields: [
                    { label: "Edificio entrada".localized(), value: resolveBuildingName(checkinBuilding) },
                    { label: "ID edificio entrada".localized(), value: checkinBuilding?.id },
                    { label: "Edificio salida".localized(), value: resolveBuildingName(checkoutBuilding) },
                    { label: "ID edificio salida".localized(), value: checkoutBuilding?.id },
                ],
            });
        }

        return result;
    };

    return {
        detail,
        loading,
        error,
        cancelReason,
        canceling,
        cancelError,
        undoingCancelation,
        undoCancelationError,
        canCancel: Boolean(detail?.isCancelable && !detail?.isCanceled),
        canUndoCancelation: Boolean(detail?.isCanceled && detail?.checkinRecordID && detail?.organizationID),
        canSubmitUndoCancelation: Boolean(detail?.isCanceled && detail?.checkinRecordID && detail?.organizationID && !undoingCancelation),
        canSubmitCancelation: Boolean(detail?.isCancelable && !detail?.isCanceled && cancelReason.trim() && !canceling),
        setCancelReason,
        cancelConciliation,
        resetCancelationDraft,
        undoCancelation,
        resetUndoCancelationDraft,
        sections: buildSections(),
        title: request?.collaboratorName ?? "Detalle de conciliación".localized(),
    };
};
